import { Instagram, Linkedin, type LucideIcon } from "lucide-react";
import type { ComponentType, SVGProps } from "react";
import { TikTokIcon } from "./icons/TikTok";
import { FEATURE_FLAGS } from "@/lib/feature-flags.keys";

/**
 * Ticket ME1: `flag` bindet einen Menüpunkt an einen Funktionsschalter. Ohne
 * eingeschalteten Schalter erscheint der Punkt in keinem Menü — im Kopf, im
 * Mobilmenü und im Fußbereich gleichermaßen.
 */
export type SubLink = {
  to: string;
  label: string;
  external?: boolean;
  flag?: string;
};
export type NavItem =
  | { type: "link"; to: string; label: string; exact?: boolean }
  | {
      type: "group";
      label: string;
      basePath: string;
      children: SubLink[];
      /**
       * Ticket WS2: zusätzliche Pfade, unter denen der Oberpunkt als aktiv gilt.
       * Nötig, weil der Blog unter `/blog` bleibt, aber zur Gruppe „Wissen“ zählt.
       */
      activePaths?: string[];
    };

/**
 * Segmentgenauer Präfixvergleich: `/blog` trifft `/blog/beitrag`, aber nicht
 * `/blogger`.
 */
export function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function isNavGroupActive(
  pathname: string,
  group: { basePath: string; activePaths?: string[] },
): boolean {
  const prefixes = group.activePaths ?? [group.basePath];
  return prefixes.some((prefix) => pathMatchesPrefix(pathname, prefix));
}


export const navItems: NavItem[] = [
  {
    type: "group",
    label: "Wahlen gewinnen",
    basePath: "/wahlen-gewinnen",
    children: [
      { to: "/wahlen-gewinnen/buergermeisterwahl", label: "Bürgermeisterwahlen" },
      { to: "/wahlen-gewinnen/direktkandidaten", label: "Direktkandidaten" },
      { to: "/wahlen-gewinnen/parteikampagnen", label: "Parteikampagnen" },
      { to: "/wahlen-gewinnen/personalratswahl", label: "Personalratswahlen" },
    ],
  },
  {
    type: "group",
    label: "Kommunikation",
    basePath: "/politisch-kommunizieren",
    children: [
      { to: "/politisch-kommunizieren/politische-kommunikation", label: "Politische Kommunikation" },
      { to: "/politisch-kommunizieren/verbandskommunikation", label: "Verbandskommunikation" },
      { to: "/politisch-kommunizieren/krisenkommunikation", label: "Krisenkommunikation" },
      { to: "/politisch-kommunizieren/gemeindekommunikation", label: "Gemeindekommunikation" },
    ],
  },
  {
    // Wissensbereich: reiner Ordnungspunkt ohne eigene Seite. Der Blog behält
    // seine Adresse `/blog`, zählt aber zur Gruppe (siehe activePaths).
    type: "group",
    label: "Wissen",
    basePath: "/wissen",
    activePaths: ["/wissen", "/blog"],
    children: [
      // Ticket WS3: Kurse zuerst – sie tragen das Angebot, die kostenlosen
      // Downloads sind der Nebeneinstieg.
      { to: "/wissen/kurse", label: "Kurse" },
      // Ticket ME1: bleibt verborgen, bis der Funktionsschalter
      // „mentorings_visible" eingeschaltet wird.
      {
        to: "/wissen/mentorings",
        label: "Mentorings",
        flag: FEATURE_FLAGS.mentoringsVisible,
      },
      // Ticket KN1: bleibt verborgen, bis der Funktionsschalter
      // „keynotes_visible" eingeschaltet wird — eigener Schalter, getrennt von
      // dem der Mentorings.
      {
        to: "/wissen/keynotes",
        label: "Keynotes",
        flag: FEATURE_FLAGS.keynotesVisible,
      },
      { to: "/wissen/downloads", label: "Downloads" },
      { to: "/blog", label: "Blog" },
      { to: "https://easyttpa.de/", label: "easyTTPA", external: true },
    ],

  },
  { type: "link", to: "/ueber-uns", label: "Agentur" },
];

export type SocialLink = {
  label: string;
  href: string;
  icon: LucideIcon | ComponentType<SVGProps<SVGSVGElement>>;
};

export const socials: SocialLink[] = [
  { label: "Instagram", href: "https://www.instagram.com/creategy.de/", icon: Instagram },
  { label: "TikTok", href: "https://www.tiktok.com/@creategy.de", icon: TikTokIcon },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/creategy-wahlkampf-verbandskommunikation/", icon: Linkedin },
];

/**
 * Reihenfolge im Desktop-Menü: „Wissen“ steht links von „Wahlen gewinnen“.
 * Tablet und Mobil nutzen weiter `navItems`, dort bleibt „Wissen“ der dritte
 * Punkt. Abgeleitet, damit ein neuer Punkt nur einmal eingetragen wird.
 */
export const desktopNavItems: NavItem[] = (() => {
  const isWissen = (item: NavItem) => item.type === "group" && item.label === "Wissen";
  return [...navItems.filter(isWissen), ...navItems.filter((i) => !isWissen(i))];
})();

/**
 * Ticket ME1: entfernt alle Unterpunkte, deren Funktionsschalter nicht in
 * `enabledFlags` steht. Reine Logik, damit die Zusicherung „ein verborgener
 * Bereich erscheint in keinem Menü" dauerhaft prüfbar bleibt.
 */
export function filterNavItems(
  items: readonly NavItem[],
  enabledFlags: readonly string[],
): NavItem[] {
  return items.map((item) => {
    if (item.type !== "group") return item;
    return {
      ...item,
      children: item.children.filter(
        (child) => !child.flag || enabledFlags.includes(child.flag),
      ),
    };
  });
}
